// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, Trash2, TriangleAlert } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { type UserRow, useDeleteUserMutation } from "@/api/hooks/use-users";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface DeleteUserDialogProps {
  user: UserRow | null;
  onClose: () => void;
}

export function DeleteUserDialog({ user, onClose }: DeleteUserDialogProps) {
  const { t } = useTranslation("back-office");
  const lastUser = useRef(user);
  if (user) lastUser.current = user;
  const display = user ?? lastUser.current;
  const deleteUser = useDeleteUserMutation(display?.publicId ?? "");

  async function onSubmit() {
    try {
      await deleteUser.mutateAsync();
      toast.success(t("users.notifications.deleted", { name: display?.fullName ?? display?.email }));
      onClose();
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={!!user} onOpenChange={() => onClose()}>
      <DialogContent id="dialog-delete-user" className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col">
        <div className="flex flex-row">
          <WizardPane variant="destructive" icon={Trash2} />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>{t("users.deleteDialog.title")}</DialogTitle>
              <DialogDescription asChild>
                <div>
                  <p>
                    {t("users.deleteDialog.prompt")}{" "}
                    {display?.fullName ? (
                      <>
                        <span className="font-semibold text-foreground">{display.fullName}</span> ({display.email})
                      </>
                    ) : (
                      <span className="font-semibold text-foreground">{display?.email}</span>
                    )}
                    ?
                  </p>
                  <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                    <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                    <span className="text-sm">{t("users.deleteDialog.warning")}</span>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button variant="destructive" onClick={onSubmit} disabled={deleteUser.isPending}>
            {deleteUser.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {t("users.deleteDialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
